import type { FC, SVGProps } from 'react';

import { colors } from '@packages/core/styles';
import { TextBadge, Typography } from '@packages/ui/shared';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import CopyIdIllustration from '../assets/copy-id-illustration.svg?react';
import PasteIdIllustration from '../assets/paste-id-illustration.svg?react';
import PlugDeviceIllustration from '../assets/plug-device-illustration.svg?react';
import { ssm5ControlStyles, ssm5TextStyles } from '../ssm5.styles';

const ILLUSTRATION_PANEL_SIZE = 144;
const STEP_BORDER_RADIUS = 8;

/** One instruction step: its copy key and its illustration. */
type InstructionStep = {
    /** Illustration shown on the left of the step. */
    Illustration: FC<SVGProps<SVGSVGElement>>;
    /** Key of the step under the `ssm5:instructions.steps` translations. */
    key: 'copyId' | 'pasteId' | 'plugDevice';
};

const INSTRUCTION_STEPS: readonly InstructionStep[] = [
    { Illustration: PlugDeviceIllustration, key: 'plugDevice' },
    { Illustration: CopyIdIllustration, key: 'copyId' },
    { Illustration: PasteIdIllustration, key: 'pasteId' },
];

const useStyles = createUseStyles({
    content: {
        alignItems: 'flex-start',
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        gap: 16,
        minWidth: 0,
        padding: '24px 24px 24px 25px',
    },
    copy: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    illustration: {
        backgroundColor: colors.grayOne,
        flexShrink: 0,
        position: 'relative',
        width: ILLUSTRATION_PANEL_SIZE,
    },
    copyId: {
        left: 13,
        position: 'absolute',
        top: 27,
    },
    pasteId: {
        left: 19,
        position: 'absolute',
        top: 30,
    },
    plugDevice: {
        left: 0,
        position: 'absolute',
        top: 0,
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        listStyle: 'none',
        margin: 0,
        padding: 0,
    },
    step: {
        backgroundColor: colors.white,
        borderRadius: STEP_BORDER_RADIUS,
        display: 'flex',
        minHeight: ILLUSTRATION_PANEL_SIZE,
        overflow: 'hidden',
        position: 'relative',
        '&::after': {
            border: `1px solid ${colors.grayThree}`,
            borderRadius: STEP_BORDER_RADIUS,
            content: '""',
            inset: 0,
            pointerEvents: 'none',
            position: 'absolute',
        },
    },
});

/**
 * The three steps that link the vehicle's Cloud Connector session to TechShare.
 *
 * @returns the ordered list of instruction steps
 */
export function Ssm5Instructions() {
    const classes = useStyles();
    const { t } = useTranslation();

    return (
        <ol className={classes.list}>
            {INSTRUCTION_STEPS.map(({ Illustration, key }) => (
                <li className={classes.step} key={key}>
                    <div aria-hidden className={classes.illustration}>
                        <Illustration className={classes[key]} />
                    </div>
                    <div className={classes.content}>
                        <TextBadge
                            style={ssm5ControlStyles.stepBadge}
                            textStyle={ssm5TextStyles.stepBadgeText}
                            typographyProps={{ color: 'blueOne', variant: 'caption6' }}
                            variant="blue"
                        >
                            {t(`ssm5:instructions.steps.${key}.badge`)}
                        </TextBadge>
                        <div className={classes.copy}>
                            <Typography style={ssm5TextStyles.bodySemibold} variant="default">
                                {t(`ssm5:instructions.steps.${key}.headline`)}
                            </Typography>
                            <Typography color="textDarkSecondary" style={ssm5TextStyles.body} variant="default">
                                {t(`ssm5:instructions.steps.${key}.description`)}
                            </Typography>
                        </div>
                    </div>
                </li>
            ))}
        </ol>
    );
}
